/**
 * The small display primitives on Ledger tokens: tooltip, separator (Divider),
 * skeleton, spinner, avatar and kbd.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Components 1: primitives".
 */
import { defineRecipe, defineSlotRecipe } from "@chakra-ui/react";

/**
 * Tooltip: the inverse surface, `--text-12`, radius 3, no blur and no arrow by
 * default. Tooltips that carry their own rich content (a muted second line, a
 * border) keep reading on the inverse surface: inside the tooltip the text and
 * border roles are pointed at the inverse text colour.
 */
export const tooltipSlotRecipe = defineSlotRecipe({
  slots: ["content", "arrow", "arrowTip"],
  base: {
    content: {
      "--tooltip-bg": "var(--color-surface-inverse)",
      "--chakra-colors-fg": "var(--color-fg-inverse)",
      "--chakra-colors-fg-2": "var(--color-fg-inverse)",
      "--chakra-colors-fg-3": "var(--color-fg-inverse)",
      "--chakra-colors-border": "var(--color-fg-inverse)",
      "--chakra-colors-border-2": "var(--color-fg-inverse)",
      // The bridge names Chakra's own recipes read.
      "--chakra-colors-fg-muted": "var(--color-fg-inverse)",
      "--chakra-colors-fg-subtle": "var(--color-fg-inverse)",
      "--chakra-colors-border-muted": "var(--color-fg-inverse)",
      background: "var(--tooltip-bg)",
      backdropFilter: "none",
      color: "var(--color-fg-inverse)",
      border: "none",
      borderRadius: "var(--radius-2)",
      boxShadow: "var(--shadow-2)",
      px: "var(--space-2)",
      py: "var(--space-1)",
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      lineHeight: "var(--leading-normal)",
      textStyle: "none",
      maxWidth: "xs",
      _open: { animationDuration: "var(--dur-2)" },
      _closed: { animationDuration: "var(--dur-1)" },
    },
    arrow: {
      "--arrow-background": "var(--color-surface-inverse)",
    },
    arrowTip: {
      borderColor: "var(--color-surface-inverse)",
    },
  },
});

/** Divider: a 1px hairline in `--color-border-2`. */
export const separatorRecipe = defineRecipe({
  base: {
    borderColor: "var(--color-border-2)",
  },
  variants: {
    orientation: {
      vertical: { width: "1px", height: "full" },
      horizontal: { height: "1px", width: "full" },
    },
    size: {
      xs: { "--separator-thickness": "1px" },
      sm: { "--separator-thickness": "1px" },
      md: { "--separator-thickness": "1px" },
      lg: { "--separator-thickness": "1px" },
    },
  },
});

/**
 * Skeleton: a surface-2 block shimmering to surface-3 once per 1.6s. Under
 * reduced motion the shimmer stops (tokens.css shortens every animation) and
 * the block stays surface-2. Show it through `useDelayedFlag(150)` so a fast
 * load never flashes it.
 */
const shimmer = {
  background: "var(--color-surface-2)",
  animation: "ledger-shimmer 1.6s ease-in-out infinite",
} as const;

/**
 * The two motions the primitives have, registered as theme keyframes: the
 * skeleton's surface-2 to surface-3 shimmer and the running Dot's pulse.
 */
export const ledgerKeyframes = {
  "ledger-shimmer": {
    "0%, 100%": { backgroundColor: "var(--color-surface-2)" },
    "50%": { backgroundColor: "var(--color-surface-3)" },
  },
  "ledger-dot-pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.35 },
  },
};

export const skeletonRecipe = defineRecipe({
  base: {
    "--skeleton-from": "var(--color-surface-2)",
    "--skeleton-to": "var(--color-surface-3)",
  },
  variants: {
    loading: {
      true: { borderRadius: "var(--radius-2)" },
    },
    variant: {
      pulse: shimmer,
      shine: { ...shimmer, backgroundImage: "none" },
    },
  },
});

/** Spinner: a ring in currentColor at 14, 16 or 20px. */
export const spinnerRecipe = defineRecipe({
  base: {
    borderWidth: "2px",
    animationDuration: "0.8s",
  },
  variants: {
    size: {
      xs: { "--spinner-size": "var(--icon-sm)" },
      sm: { "--spinner-size": "var(--icon-md)" },
      md: { "--spinner-size": "var(--icon-lg)" },
    },
  },
});

/**
 * Avatar: a pill, 24 (`2xs`) or 28 (`xs`) in dense UI, initials at `--text-11`
 * 600 on the accent tint. A caller's `colorPalette` (project marks, random
 * colour avatars) still wins: a mark keeps its colours.
 */
export const avatarSlotRecipe = defineSlotRecipe({
  slots: ["root", "image", "fallback"],
  base: {
    root: { "--avatar-radius": "var(--radius-pill)" },
    fallback: { fontWeight: "var(--weight-semibold)" },
  },
  variants: {
    size: {
      "2xs": {
        root: {
          "--avatar-size": "var(--control-sm)",
          "--avatar-font-size": "var(--text-11)",
        },
      },
      xs: {
        root: {
          "--avatar-size": "var(--control-md)",
          "--avatar-font-size": "var(--text-11)",
        },
      },
    },
  },
});

/** Kbd: mono `--text-11` on surface-2, 1px border, radius 1. */
export const kbdRecipe = defineRecipe({
  base: {
    fontFamily: "var(--font-mono)",
    fontSize: "var(--text-11)",
    fontWeight: "var(--weight-medium)",
    lineHeight: "var(--leading-tight)",
    textStyle: "none",
    color: "var(--color-fg-2)",
    background: "var(--color-surface-2)",
    border: "1px solid",
    borderColor: "var(--color-border)",
    borderBottomWidth: "1px",
    borderRadius: "var(--radius-1)",
    px: "var(--space-1)",
    minWidth: "var(--space-4)",
    justifyContent: "center",
    wordSpacing: "normal",
  },
  variants: {
    variant: {
      raised: {
        bg: "var(--color-surface-2)",
        color: "var(--color-fg-2)",
        borderBottomWidth: "1px",
        borderColor: "var(--color-border)",
      },
      outline: { bg: "transparent", color: "var(--color-fg-2)" },
      subtle: { bg: "var(--color-surface-2)", color: "var(--color-fg-2)" },
      plain: { bg: "transparent", border: "none", color: "var(--color-fg-2)" },
    },
    size: {
      sm: { height: "var(--space-4)", fontSize: "var(--text-11)" },
      md: { height: "var(--space-5)", fontSize: "var(--text-11)" },
      lg: { height: "var(--control-sm)", fontSize: "var(--text-12)" },
    },
  },
});
